import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Users, Upload, Copy, Calendar, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">{label}</div>
          <div className="font-mono text-3xl font-semibold text-gray-950 mt-3">{value?.toLocaleString() ?? 0}</div>
        </div>
        <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${accent}`}>
          <Icon size={18} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [dups, setDups] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([api.get("/stats"), api.get("/duplicates/summary")]);
        setStats(s.data);
        setDups(d.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="dashboard-page">
      <div className="mb-10 fade-up">
        <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-gray-500">Overview</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-gray-950 mt-2">Dashboard</h1>
        <p className="text-gray-600 mt-2">Daily lead intake, duplicates and quick actions.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 stagger">
        <Stat label="Total Leads" value={stats?.total_leads} icon={Users} accent="bg-[#002FA7]/10 text-[#002FA7]" testid="stat-total-leads" />
        <Stat label="New (Unique)" value={stats?.new_leads} icon={TrendingUp} accent="bg-[#00C04B]/10 text-[#00C04B]" testid="stat-new-leads" />
        <Stat label="Today" value={stats?.today_leads} icon={Calendar} accent="bg-[#007AFF]/10 text-[#007AFF]" testid="stat-today-leads" />
        <Stat label="Duplicates" value={stats?.total_duplicates} icon={Copy} accent="bg-[#FF3B30]/10 text-[#FF3B30]" testid="stat-duplicates" />
        <Stat label="Files Uploaded" value={stats?.total_files} icon={Upload} accent="bg-gray-200 text-gray-700" testid="stat-files" />
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-6">
        <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white lg:col-span-2" data-testid="duplicate-windows-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Duplicates by Window</div>
              <div className="font-display text-xl font-semibold text-gray-950 mt-1">Reference: <span className="font-mono">{dups?.reference_date || "—"}</span></div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Last 5d", dups?.last_5, "win-5"],
              ["Last 30d", dups?.last_30, "win-30"],
              ["Last 60d", dups?.last_60, "win-60"],
              ["Last 90d", dups?.last_90, "win-90"],
              ["All Time", dups?.all_time, "win-all"],
            ].map(([label, v, tid]) => (
              <div key={label} className="border border-gray-200 rounded-sm p-4" data-testid={`dup-${tid}`}>
                <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">{label}</div>
                <div className="font-mono text-2xl font-semibold text-gray-950 mt-2">{(v ?? 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-sm border border-gray-200 shadow-none p-6 bg-white" data-testid="quick-actions">
          <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-gray-500">Quick Actions</div>
          <div className="mt-4 space-y-2">
            <Link to="/upload" data-testid="quick-upload" className="block px-4 py-3 border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm transition-all">
              <div className="font-medium text-gray-950">Upload Today's File →</div>
              <div className="text-xs text-gray-500 mt-1">CSV / XLSX with yyyy-mm-dd in name</div>
            </Link>
            <Link to="/upload?mode=bulk" data-testid="quick-bulk" className="block px-4 py-3 border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm transition-all">
              <div className="font-medium text-gray-950">Bulk Import Historical →</div>
              <div className="text-xs text-gray-500 mt-1">Process hundreds of files one-by-one</div>
            </Link>
            <Link to="/merge" data-testid="quick-merge" className="block px-4 py-3 border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm transition-all">
              <div className="font-medium text-gray-950">Merge Files → One CSV →</div>
              <div className="text-xs text-gray-500 mt-1">Combine many files into one master file</div>
            </Link>
            <Link to="/send" data-testid="quick-send" className="block px-4 py-3 border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm transition-all">
              <div className="font-medium text-gray-950">Send Files via Email →</div>
              <div className="text-xs text-gray-500 mt-1">Attach renamed CSVs and email your team</div>
            </Link>
            <Link to="/files" data-testid="quick-files" className="block px-4 py-3 border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 rounded-sm transition-all">
              <div className="font-medium text-gray-950">Download / Rename →</div>
              <div className="text-xs text-gray-500 mt-1">Export by date or specific file</div>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
